import { FC, useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import styles from './stackedbar.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import type { ChartTooltipRow } from '../ChartTooltip/ChartTooltip';

// ──────────────────────────────────────────────────────────────
// STACKED BAR — segmented horizontal bar (PAR, eligibility)
// Uses shared ChartTooltip (light glassmorphism).
// ──────────────────────────────────────────────────────────────

export interface StackedBarSegment {
  label: string;
  value: number;
  displayValue?: string;
  color?: string;
  status?: 'current' | 'warn' | 'breach-light' | 'breach' | 'breach-heavy';
}

export interface StackedBarProps {
  segments: StackedBarSegment[];
  height?: number;
  showLegend?: boolean;
  className?: string;
}

const statusColors: Record<string, string> = {
  current: 'var(--chart-1, #0F2341)',
  warn: 'var(--color-warning)',
  'breach-light': '#F09595',
  breach: 'var(--color-breach)',
  'breach-heavy': '#B83030',
};

const solidSwatchColors: Record<string, string> = {
  current: 'var(--chart-1, #0F2341)',
  warn: '#F59E0B',
  'breach-light': '#F09595',
  breach: '#DC2626',
  'breach-heavy': '#B83030',
};

export const StackedBar: FC<StackedBarProps> = ({
  segments,
  height = 40,
  showLegend = false,
  className,
}) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hIdx, setHIdx] = useState<number | null>(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  const onSegEnter = useCallback((idx: number, e: React.MouseEvent<HTMLDivElement>) => {
    setHIdx(idx);
    const segRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTipPos({
      x: segRect.left + segRect.width / 2,
      y: segRect.top,
    });
  }, []);

  const onSegLeave = useCallback(() => setHIdx(null), []);

  const tipRows: ChartTooltipRow[] = hIdx !== null ? [{
    color: solidSwatchColors[segments[hIdx]?.status || 'current'] || '#6B7280',
    label: segments[hIdx]?.label || '',
    value: segments[hIdx]?.displayValue || String(segments[hIdx]?.value || 0),
    delta: total > 0 ? `${((segments[hIdx]?.value / total) * 100).toFixed(1)}%` : undefined,
    deltaDir: 'neutral',
  }] : [];

  return (
    <div ref={wrapRef} className={classNames(styles.stackedBarWrap, className, 'relative')}>
      <div className={classNames(styles.stackedBar, `h-[${height}px]`)}>
        {segments.map((seg, idx) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          const bg = seg.color || statusColors[seg.status || 'current'] || 'rgba(255,255,255,0.15)';
          return (
            <div
              key={seg.label + idx}
              className={classNames(styles.segment, { [styles.segmentDim]: hIdx !== null && hIdx !== idx })}
              style={{ flex: seg.value, background: bg }}
              onMouseEnter={(e) => onSegEnter(idx, e)}
              onMouseLeave={onSegLeave}
            >
              {pct > 4 && seg.displayValue && (
                <span className={styles.segmentLabel}>{seg.displayValue}</span>
              )}
            </div>
          );
        })}
      </div>

      <ChartTooltip
        visible={hIdx !== null}
        header={hIdx !== null ? segments[hIdx]?.label : undefined}
        rows={tipRows}
        x={tipPos.x}
        y={tipPos.y}
        flip={false}
      />

      {showLegend && (
        <div className={styles.legend}>
          {segments.map((seg, idx) => {
            const dotBg = seg.color || statusColors[seg.status || 'current'] || 'rgba(255,255,255,0.30)';
            return (
              <div key={seg.label + idx} className={styles.legendItem}>
                <div className={styles.legendDot} style={{ background: dotBg }} />
                <span className={styles.legendLabel}>{seg.label}</span>
                {seg.displayValue && (
                  <span className={styles.legendValue}>{seg.displayValue}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

StackedBar.displayName = 'StackedBar';

export default StackedBar;
